import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm, Controller, type Resolver } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/Card'
import { Badge } from 'components/ui/Badge'
import { Alert, AlertDescription, AlertTitle } from 'components/ui/Alert'
import { Modal } from 'components/ui/Modal'
import { SimpleRichTextEditor } from 'components/ui/SimpleRichTextEditor'
import { CustomSelect } from 'components/ui/CustomSelect'
import { SingleFieldWithLabels } from 'components/ui/SingleFieldWithLabels'
import { LanguageField } from 'components/ui/LanguageField'
import { FormField } from 'components/ui/FormField'
import { DraggablePieChart } from 'components/ui/DraggablePieChart'
import { Switch } from 'components/ui/switch'
import {
  ArrowLeft,
  Save,
  Percent,
  Briefcase,
  Target,
  AlertTriangle,
  Loader2
} from 'lucide-react'
import { useNotifications } from 'contexts/NotificationContext'
import { useToast } from 'contexts/ToastContext'
import { offersApi, applicationsApi, hiringProcessApi, type Offer, type Application, type HiringProcessTemplate } from 'lib/api'
import { getOfferFormSchema, OfferFormData } from 'lib/validation'
import { DepartmentSelect } from '../components/DepartmentSelect'
import { PageHeader } from 'components/layout/PageHeader'
import { RecommendedSkillsModal, type RecommendedSkillsData } from 'features/offers/components/RecommendedSkillsModal'
import { buildRecommendedSkillsDataFromAiResponse } from 'lib/offerRecommendedSkillsUtils'

const toStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []
  return value
    .map(item => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean)
}

const extractMentionSplit = (
  source: unknown,
  mentionedKey: string,
  nonMentionedKey: string
): { mentionnees: string[]; nonMentionnees: string[] } => {
  if (Array.isArray(source)) {
    return {
      mentionnees: toStringList(source),
      nonMentionnees: []
    }
  }

  if (source && typeof source === 'object') {
    const data = source as Record<string, unknown>
    return {
      mentionnees: toStringList(data[mentionedKey]),
      nonMentionnees: toStringList(data[nonMentionedKey])
    }
  }

  return {
    mentionnees: [],
    nonMentionnees: []
  }
}

export function CreateOffer() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { addNotification } = useNotifications()
  const { addToast } = useToast()
  const { t, i18n } = useTranslation()
  const [offerStatus, setOfferStatus] = useState<string>('')
  const [isPublishing, setIsPublishing] = useState<boolean>(false)
  const [isSavingDraft, setIsSavingDraft] = useState<boolean>(false)
  const [showPublishConfirm, setShowPublishConfirm] = useState(false)
  const [showZeroWeightsWarning, setShowZeroWeightsWarning] = useState(false)
  const [includeInternshipExperience, setIncludeInternshipExperience] = useState(true)
  const filterMatchingRef = useRef<any>({})
  const [applications, setApplications] = useState<Application[]>([])
  const [templates, setTemplates] = useState<HiringProcessTemplate[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
  // Track which AI matching evaluations are locked (manually edited by user)
  const [lockedEvaluations, setLockedEvaluations] = useState<Set<string>>(new Set())
  const [showRecommendedSkillsModal, setShowRecommendedSkillsModal] = useState(false)
  const [recommendedSkillsData, setRecommendedSkillsData] = useState<RecommendedSkillsData>({})
  const [createdOffer, setCreatedOffer] = useState<Offer | null>(null)
  const [isSavingRecommendedSkills, setIsSavingRecommendedSkills] = useState(false)
  const nonMentionneesRef = useRef<{
    requirements: string[]
    soft_skills: string[]
    benefits: string[]
  }>({
    requirements: [],
    soft_skills: [],
    benefits: []
  })

  const schema = useMemo(() => getOfferFormSchema(t), [t, i18n.language])

  const navigateBackToOffer = useCallback(() => {
    if (id) {
      navigate(`/offers/${id}`, { state: { initialTab: 'details' } })
    } else {
      navigate('/offers')
    }
  }, [id, navigate])

  const contractTypes = [
    { value: 'CDI', label: t('createOffer.contractTypes.cdi') },
    { value: 'CDD', label: t('createOffer.contractTypes.cdd') },
    { value: 'Stage', label: t('createOffer.contractTypes.stage') },
    { value: 'Freelance', label: t('createOffer.contractTypes.freelance') },
    { value: 'Consultant', label: t('createOffer.contractTypes.consultant') },
    { value: 'Temps partiel', label: t('createOffer.contractTypes.partTime') }
  ]

  // Experience level is collected as an integer (years of experience)

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { errors, isSubmitting }
  } = useForm<OfferFormData>({
    resolver: yupResolver(schema) as Resolver<OfferFormData>,
    mode: 'onChange', // Validate as user types
    reValidateMode: 'onChange', // Re-validate on change
    shouldFocusError: false, // Don't auto-focus on error to allow real-time validation
    defaultValues: {
      job_title: '',
      department: '',
      contract_type: '',
      // experience_level stored as string (years) to match form types
      experience_level: '',
      remote_allowed: false,
      description: '',
      description_text: '',
      description_text_source: 'manual',
      requirements: [],
      benefits: [],
      education_level: '',
      languages: [],
      certifications: [],
      main_missions: [],
      soft_skills: [],
      ai_matching: {
        hard_skills: 0,
        soft_skills: 0,
        experience_years: 0,
        niveau_etude: 0,
        languages: 0,
        certifications: 0,
        main_missions: 0,
        nice_to_have: 0
      }
    }
  })

  // Track whether the form currently has errors
  const hadErrorsRef = useRef(false)
  useEffect(() => {
    hadErrorsRef.current = Object.keys(errors).length > 0
  }, [errors])

  useEffect(() => {
    // Re-validate only when language changes AND there are existing errors
    if (hadErrorsRef.current) {
      trigger()
    }
  }, [i18n.language, trigger])

  // Load available hiring process templates for selection
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const res = await hiringProcessApi.getTemplates()
        const items = Array.isArray(res.data) ? res.data : (res.data as any).results || []
        const active = items.filter((tpl: HiringProcessTemplate) => tpl.is_active)
        setTemplates(active)
        const defaultTemplate = active.find(tpl => tpl.is_default)
        if (!id) {
          setSelectedTemplateId(defaultTemplate ? defaultTemplate.id : active[0]?.id ?? null)
        }
      } catch (error) {
        console.error('Error loading hiring process templates:', error)
        setTemplates([])
      }
    }
    loadTemplates()
  }, [id])

  // When editing, load offer and prefill form
  useEffect(() => {
    const load = async () => {
      if (!id) return
      const res = await offersApi.getById(Number(id))
      let o = res.data as Offer

      // Closed / archived offers cannot be edited via the create/edit form
      if (o.status === 'offer_closed' || o.status === 'closed') {
        addToast({
          type: 'warning',
          title: t('common.warning', 'Warning'),
          message: t(
            'offerDetail.cannotEditClosedOffer',
            'Cannot modify a closed offer.'
          ),
          duration: 5000
        })
        navigate(`/offers/${o.id}`, { replace: true })
        return
      }

      // Check if any weight exceeds 10 and normalize if needed
      if ((o as any).ai_matching_weights) {
        const weights = (o as any).ai_matching_weights
        const maxWeight = Math.max(...Object.values(weights).filter(v => typeof v === 'number') as number[])

        if (maxWeight > 10) {
          // Divide all weights by 10 and save to database
          const normalizedWeights = Object.entries(weights).reduce((acc, [key, value]) => {
            acc[key as string] = typeof value === 'number' ? Math.round(value / 10) : value
            return acc
          }, {} as Record<string, any>)

          // Update weights in database
          try {
            await offersApi.update(Number(id), {
              ...o,
              ai_matching_weights: normalizedWeights
            })

            // Update local state with normalized weights
            o = {
              ...o,
              ai_matching_weights: normalizedWeights
            }
          } catch (error) {
            console.error('Error normalizing weights:', error)
          }
        }
      }

      filterMatchingRef.current = (o as any).filter_matching || {}
      setIncludeInternshipExperience((o as any).filter_matching?.stage_exclu !== 1)

      setOfferStatus((o as any).status || '')
      const requirementsSplit = extractMentionSplit(
        (o as any).requirements,
        'hard_skills_mentionnées',
        'hard_skills_non_mentionnées'
      )
      const softSkillsSplit = extractMentionSplit(
        (o as any).soft_skills,
        'soft_skills_mentionnées',
        'soft_skills_non_mentionnées'
      )
      const benefitsSplit = extractMentionSplit(
        (o as any).benefits,
        'nice_to_have_mentionnées',
        'nice_to_have_non_mentionnées'
      )

      nonMentionneesRef.current = {
        requirements: requirementsSplit.nonMentionnees,
        soft_skills: softSkillsSplit.nonMentionnees,
        benefits: benefitsSplit.nonMentionnees
      }

      setSelectedTemplateId((o as any).hiring_template ?? null)
      const formValues: OfferFormData = {
        job_title: o.job_title || '',
        department: (o as any).department || '',
        contract_type: (o as any).contract_type || '',
        // ensure experience_level is a number when loading existing offer
        experience_level: (o as any).experience_level ?? 0,
        remote_allowed: Boolean((o as any).remote_allowed),
        description: o.description || '',
        description_text: o.description_text || o.description || '',
        description_text_source: (o.description_text_source ?? 'manual') as Offer['description_text_source'],
        requirements: requirementsSplit.mentionnees,
        benefits: benefitsSplit.mentionnees,
        education_level: (o as any).education_level || '',
        languages: Array.isArray((o as any).languages) ? (o as any).languages : [],
        certifications: Array.isArray((o as any).certifications) ? (o as any).certifications : [],
        main_missions: Array.isArray((o as any).main_missions) ? (o as any).main_missions : [],
        soft_skills: softSkillsSplit.mentionnees,
        ai_matching: (o as any).ai_matching_weights || {
          hard_skills: 2,
          soft_skills: 2,
          experience_years: 2,
          niveau_etude: 2,
          languages: 1,
          certifications: 1,
          main_missions: 1,
          nice_to_have: 0
        }
      }
      reset(formValues)
      // Reset locked evaluations when loading existing offer
      setLockedEvaluations(new Set())
    }
    load()
  }, [id, setValue, reset, addToast, t, navigate])

  // Fetch applications when editing to check if candidates are in 'applied' status
  useEffect(() => {
    const loadApplications = async () => {
      if (!id) {
        setApplications([])
        return
      }
      try {
        const response = await applicationsApi.getByOffer(Number(id))
        setApplications(response.data.applications || [])
      } catch (error) {
        console.error('Error fetching applications:', error)
        setApplications([])
      }
    }
    loadApplications()
  }, [id])

  // Check if matching has been applied (candidates moved beyond 'applied' status) - blocks editing of sensitive fields
  const hasMatchingApplied = useMemo(() => {
    if (!id) return false // Only check when editing
    // Check if any application has moved beyond 'applied' status (scanning, preselection, interview, etc.)
    return applications.some(app => app.status !== 'applied')
  }, [applications, id])

  const getCleanedWeightsSum = () => {
    const data = watch()
    const cleanedRequirements = data.requirements?.filter(req => req?.trim()) || []
    const cleanedBenefits = data.benefits?.filter(benefit => benefit?.trim()) || []
    const cleanedLanguages = data.languages?.filter(lang => lang.language?.trim() && lang.level?.trim()) || []
    const cleanedCertifications = data.certifications?.filter(cert => cert?.trim()) || []
    const cleanedMainMissions = data.main_missions?.filter(mission => mission?.trim()) || []
    const cleanedSoftSkills = data.soft_skills?.filter(skill => skill?.trim()) || []

    const adjustedAiMatching = { ...data.ai_matching } as any
    if (cleanedBenefits.length === 0) adjustedAiMatching.nice_to_have = 0
    if (!data.education_level || !data.education_level.trim()) adjustedAiMatching.niveau_etude = 0
    if (cleanedLanguages.length === 0) adjustedAiMatching.languages = 0
    if (cleanedRequirements.length === 0) adjustedAiMatching.hard_skills = 0
    if (cleanedCertifications.length === 0) adjustedAiMatching.certifications = 0
    if (cleanedMainMissions.length === 0) adjustedAiMatching.main_missions = 0
    if (cleanedSoftSkills.length === 0) adjustedAiMatching.soft_skills = 0
    if (!data.experience_level || data.experience_level.toString().trim() === '') adjustedAiMatching.experience_years = 0

    return Object.values(adjustedAiMatching).reduce((sum: number, val: any) => sum + (Number(val) || 0), 0)
  }

  const getBackendErrorMessage = (error: any, fallbackKey: string) => {
    if (error?.response?.data) {
      const errorData = error.response.data

      // If it is a string error
      if (typeof errorData === 'string') return errorData

      // If there's an explicit error or detail key
      if (errorData.error) return errorData.error
      if (errorData.detail) return errorData.detail

      // Check for field-specific errors
      if (typeof errorData === 'object' && !Array.isArray(errorData)) {
        const firstField = Object.keys(errorData)[0]
        if (firstField) {
          const fieldError = errorData[firstField]
          const msg = Array.isArray(fieldError) ? fieldError[0] : fieldError
          if (typeof msg === 'string' && msg.trim()) {
            return msg
          }
        }
      }
    }
    return t(fallbackKey)
  }

  const publishDraft = async () => {
    if (getCleanedWeightsSum() === 0) {
      setShowZeroWeightsWarning(true)
      return
    }
    // Show confirmation modal
    setShowPublishConfirm(true)
  }

  const handleAcceptRecommendedSkills = async (
    selected: {
      hard_skills_non_mentionnées: string[]
      soft_skills_non_mentionnées: string[]
      nice_to_have_non_mentionnées: string[]
    },
    weights?: {
      hard_skills: number
      soft_skills: number
      nice_to_have: number
    }
  ) => {
    if (!createdOffer) return
    setIsSavingRecommendedSkills(true)
    try {
      const payload: any = {
        requirements: {
          hard_skills_mentionnées: createdOffer.requirements?.hard_skills_mentionnées ?? [],
          hard_skills_non_mentionnées: selected.hard_skills_non_mentionnées
        },
        soft_skills: {
          soft_skills_mentionnées: createdOffer.soft_skills?.soft_skills_mentionnées ?? [],
          soft_skills_non_mentionnées: selected.soft_skills_non_mentionnées
        },
        benefits: {
          nice_to_have_mentionnées: createdOffer.benefits?.nice_to_have_mentionnées ?? [],
          nice_to_have_non_mentionnées: selected.nice_to_have_non_mentionnées
        }
      }
      if (weights) {
        payload.ai_matching_weights = weights
      }
      const response = await offersApi.updateAiSkills(createdOffer.id, payload)
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('recommendedSkills.successMessage', 'Les compétences sélectionnées ont été ajoutées avec succès à l’offre.')
      })
      setShowRecommendedSkillsModal(false)
      navigate(`/offers/${createdOffer.id}`, { state: { initialTab: 'details' } })
    } catch (err) {
      console.error('Error saving recommended skills:', err)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('recommendedSkills.errorMessage', 'Impossible d’enregistrer les compétences.')
      })
    } finally {
      setIsSavingRecommendedSkills(false)
    }
  }

  const confirmPublish = async () => {
    if (!id) return
    try {
      setIsPublishing(true)
      const response = await offersApi.update(Number(id), { status: 'published' })
      const updatedOffer = response.data
      setOfferStatus('published')
      addNotification({
        type: 'update',
        title: t('common.success'),
        message: t('notifications.messages.offerPublished', { title: watch('job_title') || '' }),
        titleKey: 'common.success',
        messageKey: 'notifications.messages.offerPublished',
        messageParams: { title: watch('job_title') || '' },
        priority: 'medium',
        category: 'offer'
      })

      // Look for recommended skills
      let freshOffer = updatedOffer
      let freshAiResponse = updatedOffer?.ai_response
      
      if (!freshAiResponse) {
        try {
          const freshResponse = await offersApi.getById(Number(id))
          freshOffer = freshResponse.data
          freshAiResponse = freshOffer?.ai_response
        } catch {
          // ignore
        }
      }

      const recommendedSkills = buildRecommendedSkillsDataFromAiResponse(freshAiResponse)
      if (recommendedSkills && freshOffer) {
        setRecommendedSkillsData(recommendedSkills)
        setCreatedOffer(freshOffer)
        setShowPublishConfirm(false)
        setShowRecommendedSkillsModal(true)
        return
      }

      setShowPublishConfirm(false)
      navigateBackToOffer()
    } catch (error: any) {
      addNotification({
        type: 'alert',
        title: t('common.error'),
        message: getBackendErrorMessage(error, 'notifications.messages.offerPublishFailed'),
        titleKey: 'common.error',
        messageKey: undefined,
        priority: 'high',
        category: 'offer'
      })
    } finally {
      setIsPublishing(false)
    }
  }

  const aiMatching = watch('ai_matching') || {
    hard_skills: 0,
    soft_skills: 0,
    experience_years: 0,
    niveau_etude: 0,
    languages: 0,
    certifications: 0,
    main_missions: 0,
    nice_to_have: 0
  }

  // Watch form fields to determine which evaluations should be active
  const benefits = watch('benefits') || []
  const requirements = watch('requirements') || []
  const educationLevel = watch('education_level') || ''
  const languages = watch('languages') || []
  const certifications = watch('certifications') || []
  const mainMissions = watch('main_missions') || []
  const softSkills = watch('soft_skills') || []
  const experienceLevel = watch('experience_level') || ''

  // Determine which evaluations should be active based on form data
  const getActiveEvaluations = useCallback(() => {
    const active: string[] = []

    // Map form fields to AI matching evaluations
    if (benefits && benefits.length > 0 && benefits.some((b: string) => b?.trim())) {
      active.push('nice_to_have')
    }
    if (educationLevel && educationLevel.trim()) {
      active.push('niveau_etude')
    }
    if (languages && languages.length > 0 && languages.some((l: any) => l?.language?.trim() && l?.level?.trim())) {
      active.push('languages')
    }
    if (requirements && requirements.length > 0 && requirements.some((r: string) => r?.trim())) {
      active.push('hard_skills')
    }
    if (certifications && certifications.length > 0 && certifications.some((c: string) => c?.trim())) {
      active.push('certifications')
    }
    if (mainMissions && mainMissions.length > 0 && mainMissions.some((m: string) => m?.trim())) {
      active.push('main_missions')
    }
    if (softSkills && softSkills.length > 0 && softSkills.some((s: string) => s?.trim())) {
      active.push('soft_skills')
    }
    if (experienceLevel && experienceLevel.toString().trim() && experienceLevel.toString().trim() !== '0') {
      active.push('experience_years')
    }

    return active
  }, [benefits, educationLevel, languages, requirements, certifications, mainMissions, softSkills, experienceLevel])

  // Get active evaluations
  const activeEvaluations = useMemo(() => getActiveEvaluations(), [getActiveEvaluations])

  // Function to redistribute percentages evenly among active evaluations
  // This is called automatically when form data changes - does NOT lock evaluations
  const redistributeAiMatchingValues = useCallback(() => {
    // Get current values directly from form to avoid dependency on aiMatching
    const currentAiMatching = getValues('ai_matching') || {
      hard_skills: 0,
      soft_skills: 0,
      experience_years: 0,
      niveau_etude: 0,
      languages: 0,
      certifications: 0,
      main_missions: 0,
      nice_to_have: 0
    }

    // Get current locked evaluations (use the state directly via closure)
    const currentLocked = lockedEvaluations

    if (activeEvaluations.length === 0) {
      // If no active evaluations, set all to 0
      const allKeys = ['hard_skills', 'soft_skills', 'experience_years', 'niveau_etude', 'languages', 'certifications', 'main_missions', 'nice_to_have']
      const updates: Record<string, number> = {}
      allKeys.forEach(key => {
        updates[`ai_matching.${key}`] = 0
      })
      // Batch all updates
      Object.entries(updates).forEach(([path, value]) => {
        setValue(path as any, value, { shouldValidate: false, shouldDirty: false })
      })
      // Clear locks when no active evaluations
      setLockedEvaluations(new Set())
      return
    }

    // Get unlocked active evaluations (those that haven't been manually edited)
    const unlockedActiveEvaluations = activeEvaluations.filter(key => !currentLocked.has(key))

    // If all active evaluations are locked, don't redistribute
    if (unlockedActiveEvaluations.length === 0) {
      return
    }

    // Calculate total of locked evaluations
    const lockedTotal = activeEvaluations
      .filter(key => currentLocked.has(key))
      .reduce((sum, key) => sum + (currentAiMatching[key as keyof typeof currentAiMatching] || 0), 0)

    // Calculate equal distribution for unlocked evaluations (using a total of 10 instead of 100)
    const availableForUnlocked = Math.max(0, 10 - lockedTotal)
    const equalValue = Math.floor(availableForUnlocked / unlockedActiveEvaluations.length)
    const remainder = availableForUnlocked - (equalValue * unlockedActiveEvaluations.length)

    // Prepare all updates (only if value actually changed)
    const updates: Record<string, number> = {}
    const allKeys = ['hard_skills', 'soft_skills', 'experience_years', 'niveau_etude', 'languages', 'certifications', 'main_missions', 'nice_to_have']

    allKeys.forEach(key => {
      if (activeEvaluations.includes(key)) {
        if (currentLocked.has(key)) {
          // Keep locked values unchanged - don't update
          return
        } else {
          // Distribute evenly among unlocked, with remainder added to first unlocked evaluation
          const newValue = key === unlockedActiveEvaluations[0] ? equalValue + remainder : equalValue
          const currentValue = currentAiMatching[key as keyof typeof currentAiMatching] || 0
          // Only update if value actually changed
          if (newValue !== currentValue) {
            updates[`ai_matching.${key}`] = newValue
          }
        }
      } else {
        const currentValue = currentAiMatching[key as keyof typeof currentAiMatching] || 0
        // Only update if value actually changed
        if (currentValue !== 0) {
          updates[`ai_matching.${key}`] = 0
        }
      }
    })

    // Only update if there are actual changes
    if (Object.keys(updates).length > 0) {
      // Batch all updates with shouldValidate: false to prevent triggering validation loops
      Object.entries(updates).forEach(([path, value]) => {
        setValue(path as any, value, { shouldValidate: false, shouldDirty: false })
      })
    }
  }, [activeEvaluations, lockedEvaluations, getValues, setValue])

  // Redistribute when active evaluations change (only for new offers, not when editing)
  // Use a ref to prevent infinite loops
  const isRedistributingRef = useRef(false)
  const lastActiveEvaluationsRef = useRef<string[]>([])

  useEffect(() => {
    if (!id) {
      // Only auto-redistribute for new offers
      // Check if activeEvaluations actually changed
      const activeEvalsStr = JSON.stringify([...activeEvaluations].sort())
      const lastActiveEvalsStr = JSON.stringify([...lastActiveEvaluationsRef.current].sort())

      if (activeEvalsStr !== lastActiveEvalsStr && !isRedistributingRef.current) {
        isRedistributingRef.current = true
        lastActiveEvaluationsRef.current = [...activeEvaluations]
        redistributeAiMatchingValues()
        // Reset flag after state updates complete
        setTimeout(() => {
          isRedistributingRef.current = false
        }, 0)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEvaluations, id]) // redistributeAiMatchingValues intentionally excluded to prevent infinite loop

  // Clear locks for evaluations that are no longer active
  useEffect(() => {
    setLockedEvaluations(prev => {
      const newLocked = new Set<string>()
      prev.forEach(key => {
        if (activeEvaluations.includes(key)) {
          newLocked.add(key)
        }
      })
      return newLocked
    })
  }, [activeEvaluations])
  const defaultDepartments = useMemo(() => [
    t('createOffer.departments.development'),
    t('createOffer.departments.design'),
    t('createOffer.departments.marketing'),
    t('createOffer.departments.sales'),
    t('createOffer.departments.hr'),
    t('createOffer.departments.finance'),
    t('createOffer.departments.support'),
    t('createOffer.departments.other')
  ], [t])


  const clampAiMatchingWeight = (value: number) => {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) return 0
    return Math.max(0, Math.min(10, Math.round(numericValue)))
  }

  // Function to update AI matching values without auto-adjustment
  // Keep values fixed unless explicitly edited
  const handleAiMatchingWeightChange = (field: string, value: number) => {
    setValue(`ai_matching.${field}` as any, clampAiMatchingWeight(value))
    setTimeout(() => trigger('ai_matching'), 100)
  }

  const createOffer = async (data: OfferFormData, status: 'draft' | 'published') => {
    try {
      // Prepare cleaned data
      const cleanedRequirements = data.requirements?.filter(req => req?.trim()) || []
      const cleanedBenefits = data.benefits?.filter(benefit => benefit?.trim()) || []
      const cleanedLanguages = data.languages?.filter(lang => lang.language?.trim() && lang.level?.trim()) || []
      const cleanedCertifications = data.certifications?.filter(cert => cert?.trim()) || []
      const cleanedMainMissions = data.main_missions?.filter(mission => mission?.trim()) || []
      const cleanedSoftSkills = data.soft_skills?.filter(skill => skill?.trim()) || []

      // Adjust AI matching weights: set to 0 for empty fields
      const adjustedAiMatching = { ...data.ai_matching } as Offer['ai_matching_weights']

      if (cleanedBenefits.length === 0) {
        adjustedAiMatching.nice_to_have = 0
      }
      if (!data.education_level || !data.education_level.trim()) {
        adjustedAiMatching.niveau_etude = 0
      }
      if (cleanedLanguages.length === 0) {
        adjustedAiMatching.languages = 0
      }
      if (cleanedRequirements.length === 0) {
        adjustedAiMatching.hard_skills = 0
      }
      if (cleanedCertifications.length === 0) {
        adjustedAiMatching.certifications = 0
      }
      if (cleanedMainMissions.length === 0) {
        adjustedAiMatching.main_missions = 0
      }
      if (cleanedSoftSkills.length === 0) {
        adjustedAiMatching.soft_skills = 0
      }
      if (!data.experience_level || data.experience_level.toString().trim() === '') {
        adjustedAiMatching.experience_years = 0
      }

      if (status === 'published') {
        const sumWeights = Object.values(adjustedAiMatching).reduce((sum: number, val: any) => sum + (Number(val) || 0), 0)
        if (sumWeights === 0) {
          setShowZeroWeightsWarning(true)
          return
        }
      }

      const offerData = {
        job_title: data.job_title,
        description: data.description_text || data.description,
        description_text: data.description_text || data.description,
        description_text_source: data.description_text_source || 'manual',
        requirements: {
          hard_skills_mentionnées: cleanedRequirements,
          hard_skills_non_mentionnées: nonMentionneesRef.current.requirements
        },
        benefits: {
          nice_to_have_mentionnées: cleanedBenefits,
          nice_to_have_non_mentionnées: nonMentionneesRef.current.benefits
        },
        department: data.department,
        contract_type: data.contract_type,
        experience_level: data.experience_level,
        remote_allowed: data.remote_allowed || false,
        education_level: data.education_level,
        languages: cleanedLanguages.map(lang => ({
          language: (lang.language || '').trim(),
          level: (lang.level || '').trim(),
        })),
        certifications: cleanedCertifications,
        main_missions: cleanedMainMissions,
        soft_skills: {
          soft_skills_mentionnées: cleanedSoftSkills,
          soft_skills_non_mentionnées: nonMentionneesRef.current.soft_skills
        },
        ai_matching_weights: adjustedAiMatching,
        status: status,
        hiring_template: selectedTemplateId,
        filter_matching: {
          ...filterMatchingRef.current,
          stage_exclu: includeInternshipExperience ? 0 : 1
        }
      }

      const response = await offersApi.create(offerData)
      const createdOfferData = response.data

      addNotification({
        type: 'update',
        title: t('common.success'),
        message: status === 'published'
          ? t('notifications.messages.offerPublished', { title: data.job_title })
          : t('notifications.messages.offerSavedAsDraft', { title: data.job_title }),
        titleKey: 'common.success',
        messageKey: status === 'published'
          ? 'notifications.messages.offerPublished'
          : 'notifications.messages.offerSavedAsDraft',
        messageParams: { title: data.job_title },
        priority: 'medium',
        category: 'offer'
      })

      if (status === 'published') {
        let freshOffer = createdOfferData
        let freshAiResponse = createdOfferData?.ai_response

        if (!freshAiResponse && createdOfferData?.id) {
          try {
            const freshResponse = await offersApi.getById(createdOfferData.id)
            freshOffer = freshResponse.data
            freshAiResponse = freshOffer?.ai_response
          } catch {
            // ignore
          }
        }

        const recommendedSkills = buildRecommendedSkillsDataFromAiResponse(freshAiResponse)
        if (recommendedSkills && freshOffer) {
          setRecommendedSkillsData(recommendedSkills)
          setCreatedOffer(freshOffer)
          setShowRecommendedSkillsModal(true)
          return
        }
      }

      navigateBackToOffer()
    } catch (error: any) {
      console.error('Error creating offer:', error)
      addNotification({
        type: 'alert',
        title: t('common.error'),
        message: getBackendErrorMessage(error, 'notifications.messages.offerPublishFailed'),
        titleKey: 'common.error',
        messageKey: undefined,
        priority: 'high',
        category: 'offer'
      })
    } finally {
      // Reset saving draft state when done
      if (status === 'draft') {
        setIsSavingDraft(false)
      }
    }
  }

  const onSubmit = async (data: OfferFormData) => {
    console.log('Form submitted with data:', data)
    console.log('Is edit mode (id exists):', !!id)
    console.log('Current form errors:', errors)
    console.log('Form is submitting:', isSubmitting)
    console.log('Active evaluations:', activeEvaluations)

    if (id) {
      let offerData: any

      if (hasMatchingApplied) {
        // After matching: only allow editing insensitive fields
        offerData = {
          job_title: data.job_title,
          department: data.department,
          contract_type: data.contract_type,
          remote_allowed: data.remote_allowed || false,
          filter_matching: {
            ...filterMatchingRef.current,
            stage_exclu: includeInternshipExperience ? 0 : 1
          }
        }
      } else {
        // Before matching or no candidates: allow editing all fields
        // Prepare cleaned data
        const cleanedRequirements = data.requirements?.filter(req => req?.trim()) || []
        const cleanedBenefits = data.benefits?.filter(benefit => benefit?.trim()) || []
        const cleanedLanguages = data.languages?.filter(lang => lang.language?.trim() && lang.level?.trim()) || []
        const cleanedCertifications = data.certifications?.filter(cert => cert?.trim()) || []
        const cleanedMainMissions = data.main_missions?.filter(mission => mission?.trim()) || []
        const cleanedSoftSkills = data.soft_skills?.filter(skill => skill?.trim()) || []

        // Adjust AI matching weights: set to 0 for empty fields
        const adjustedAiMatching = { ...data.ai_matching } as Offer['ai_matching_weights']

        if (cleanedBenefits.length === 0) {
          adjustedAiMatching.nice_to_have = 0
        }
        if (!data.education_level || !data.education_level.trim()) {
          adjustedAiMatching.niveau_etude = 0
        }
        if (cleanedLanguages.length === 0) {
          adjustedAiMatching.languages = 0
        }
        if (cleanedRequirements.length === 0) {
          adjustedAiMatching.hard_skills = 0
        }
        if (cleanedCertifications.length === 0) {
          adjustedAiMatching.certifications = 0
        }
        if (cleanedMainMissions.length === 0) {
          adjustedAiMatching.main_missions = 0
        }
        if (cleanedSoftSkills.length === 0) {
          adjustedAiMatching.soft_skills = 0
        }
        if (!data.experience_level || data.experience_level.toString().trim() === '') {
          adjustedAiMatching.experience_years = 0
        }

        if (offerStatus === 'published') {
          const sumWeights = Object.values(adjustedAiMatching).reduce((sum: number, val: any) => sum + (Number(val) || 0), 0)
          if (sumWeights === 0) {
            setShowZeroWeightsWarning(true)
            return
          }
        }

        offerData = {
          job_title: data.job_title,
          description: data.description_text || data.description,
          description_text: data.description_text || data.description,
          description_text_source: data.description_text_source || 'manual',
          requirements: {
            hard_skills_mentionnées: cleanedRequirements,
            hard_skills_non_mentionnées: nonMentionneesRef.current.requirements
          },
          benefits: {
            nice_to_have_mentionnées: cleanedBenefits,
            nice_to_have_non_mentionnées: nonMentionneesRef.current.benefits
          },
          department: data.department,
          contract_type: data.contract_type,
          experience_level: data.experience_level,
          remote_allowed: data.remote_allowed || false,
          education_level: data.education_level,
          languages: cleanedLanguages.map(lang => ({
            language: (lang.language || '').trim(),
            level: (lang.level || '').trim(),
          })),
          certifications: cleanedCertifications,
          main_missions: cleanedMainMissions,
          soft_skills: {
            soft_skills_mentionnées: cleanedSoftSkills,
            soft_skills_non_mentionnées: nonMentionneesRef.current.soft_skills
          },
          ai_matching_weights: adjustedAiMatching,
          filter_matching: {
            ...filterMatchingRef.current,
            stage_exclu: includeInternshipExperience ? 0 : 1
          }
        }
      }

      console.log('Updating offer with data:', offerData)

      try {
        await offersApi.update(Number(id), offerData)
        console.log('Offer updated successfully')

        // Reload offer data to refresh the form with latest values
        const res = await offersApi.getById(Number(id))
        let o = res.data as Offer

        // Check if any weight exceeds 10 and normalize if needed
        if ((o as any).ai_matching_weights) {
          const weights = (o as any).ai_matching_weights
          const maxWeight = Math.max(...Object.values(weights).filter(v => typeof v === 'number') as number[])

          if (maxWeight > 10) {
            // Divide all weights by 10 and save to database
            const normalizedWeights = Object.entries(weights).reduce((acc, [key, value]) => {
              acc[key as string] = typeof value === 'number' ? Math.round(value / 10) : value
              return acc
            }, {} as Record<string, any>)

            // Update weights in database
            try {
              await offersApi.update(Number(id), {
                ...o,
                ai_matching_weights: normalizedWeights
              })

              // Update local state with normalized weights
              o = {
                ...o,
                ai_matching_weights: normalizedWeights
              }
            } catch (error) {
              console.error('Error normalizing weights:', error)
            }
          }
        }

        const requirementsSplit = extractMentionSplit(
          (o as any).requirements,
          'hard_skills_mentionnées',
          'hard_skills_non_mentionnées'
        )
        const softSkillsSplit = extractMentionSplit(
          (o as any).soft_skills,
          'soft_skills_mentionnées',
          'soft_skills_non_mentionnées'
        )
        const benefitsSplit = extractMentionSplit(
          (o as any).benefits,
          'nice_to_have_mentionnées',
          'nice_to_have_non_mentionnées'
        )

        nonMentionneesRef.current = {
          requirements: requirementsSplit.nonMentionnees,
          soft_skills: softSkillsSplit.nonMentionnees,
          benefits: benefitsSplit.nonMentionnees
        }

        const formValues: OfferFormData = {
          job_title: o.job_title || '',
          department: (o as any).department || '',
          contract_type: (o as any).contract_type || '',
          experience_level: (o as any).experience_level ?? 0,
          remote_allowed: Boolean((o as any).remote_allowed),
          description: o.description || '',
          description_text: o.description_text || o.description || '',
          description_text_source: (o.description_text_source ?? 'manual') as Offer['description_text_source'],
          requirements: requirementsSplit.mentionnees,
          benefits: benefitsSplit.mentionnees,
          education_level: (o as any).education_level || '',
          languages: Array.isArray((o as any).languages) ? (o as any).languages : [],
          certifications: Array.isArray((o as any).certifications) ? (o as any).certifications : [],
          main_missions: Array.isArray((o as any).main_missions) ? (o as any).main_missions : [],
          soft_skills: softSkillsSplit.mentionnees,
          ai_matching: (o as any).ai_matching_weights || {
            hard_skills: 2,
            soft_skills: 2,
            experience_years: 2,
            niveau_etude: 2,
            languages: 1,
            certifications: 1,
            main_missions: 1,
            nice_to_have: 0
          }
        }
        reset(formValues)

        // Show success toast alert
        addToast({
          type: 'success',
          title: t('common.success'),
          message: t('notifications.messages.offerUpdated', { title: data.job_title }),
          duration: 5000
        })

        // Also add notification
        addNotification({
          type: 'success',
          title: t('common.success'),
          message: t('notifications.messages.offerUpdated', { title: data.job_title }),
          titleKey: 'common.success',
          messageKey: 'notifications.messages.offerUpdated',
          messageParams: { title: data.job_title },
          priority: 'medium',
          category: 'offer'
        })

        // Stay on the same page - no redirect
      } catch (error: any) {
        console.error('Error updating offer:', error)
        addNotification({
          type: 'alert',
          title: t('common.error'),
          message: getBackendErrorMessage(error, 'notifications.messages.offerUpdateFailed'),
          titleKey: 'common.error',
          messageKey: undefined,
          priority: 'high',
          category: 'offer'
        })
      }
    } else {
      await createOffer(data, 'published')
    }
  }

  const draftRequiredFields: Array<keyof OfferFormData> = ['job_title']

  const onSaveAsDraft = async () => {
    // Only validate fields that are actually required to persist a draft
    const isDraftValid = await trigger(draftRequiredFields, { shouldFocus: true })
    if (isDraftValid) {
      setIsSavingDraft(true)
      const formData = watch()
      await createOffer(formData, 'draft')
    }
    // If validation fails, errors will be shown automatically
  }

  // Handle invalid submissions: surface errors and scroll to first error
  const onSubmitErrors = (formErrors: typeof errors) => {
    try {
      // No global notification; rely on field-level errors
      // Focus/scroll to the first field with an error
      const firstErrorKey = Object.keys(formErrors)[0]
      if (firstErrorKey) {
        // react-hook-form focuses registered input by name when using setFocus,
        // but we can also attempt to scroll to the element if present
        const el = document.querySelector(`[name="${firstErrorKey}"]`) as HTMLElement | null
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          el.focus?.()
        }
      }
    } catch {
      // no-op
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6 md:py-8 max-w-6xl">
        {/* Header */}
        <div className="flex flex-col min-[1150px]:flex-row min-[1150px]:items-start min-[1150px]:justify-between gap-4 mb-6 md:mb-8">
          <div className="flex items-start space-x-3 sm:space-x-4 flex-1 min-w-0">
            <Button
              variant="outline"
              size="sm"
              onClick={navigateBackToOffer}
              className="flex items-center space-x-2 flex-shrink-0 mt-1"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{t('common.back')}</span>
            </Button>
            <PageHeader
              className="min-w-0 flex-1"
              title={id ? t('offers.editOfferTitle', 'Edit Offer') : t('createOffer.title')}
            />
          </div>

          {/* Action Buttons - Two Rows from mobile to 1150px, One Row on Desktop (1150px+) */}
          <div className="flex flex-col min-[1150px]:flex-row gap-3 flex-shrink-0">
            {/* First Row: Cancel and Save as Draft (50% each) */}
            <div className="flex gap-2 sm:gap-3 min-[1150px]:gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={navigateBackToOffer}
                className="flex-1 min-[1150px]:flex-initial min-[1150px]:w-auto h-10 whitespace-nowrap"
              >
                {t('common.cancel')}
              </Button>
              {!id && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onSaveAsDraft}
                  disabled={isSubmitting || isSavingDraft}
                  loading={isSavingDraft}
                  className="flex-1 min-[1150px]:flex-initial min-[1150px]:w-auto h-10 flex items-center justify-center space-x-2 bg-green-500 text-white border-green-500 hover:bg-green-600 hover:border-green-600 hover:text-white whitespace-nowrap"
                >
                  <Save className="h-4 w-4 flex-shrink-0" />
                  <span>
                    {isSavingDraft ? t('common.saving') : t('createOffer.form.saveAsDraft')}
                  </span>
                </Button>
              )}
              {id && offerStatus === 'draft' && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={publishDraft}
                  disabled={isPublishing}
                  className="flex-1 min-[1150px]:flex-initial min-[1150px]:w-auto h-10 flex items-center justify-center space-x-2 bg-green-600 text-white border-green-600 hover:bg-green-700 hover:border-green-700 hover:text-white whitespace-nowrap"
                >
                  <Save className="h-4 w-4 flex-shrink-0" />
                  <span>
                    {isPublishing ? t('common.saving') : t('createOffer.form.publish')}
                  </span>
                </Button>
              )}
            </div>
            {/* Second Row: Publish/Update Button (full width on mobile/tablet) */}
            <Button
              type="submit"
              form="create-offer-form"
              disabled={isSubmitting}
              className="w-full min-[1150px]:w-auto h-10 flex items-center justify-center space-x-2 whitespace-nowrap"
            >
              <Save className="h-4 w-4 flex-shrink-0" />
              <span>
                {isSubmitting ? t('common.saving') : (id ? t('common.update') : t('createOffer.form.publish'))}
              </span>
            </Button>
          </div>
        </div>

        {/* Hiring template selection card (new offers and edit) */}
        <div className="mb-6">
          <Card className="border bg-card shadow-sm">
            <CardHeader className="border-b">
              <CardTitle className="text-sm font-semibold">
                {t('hiringProcess.template', 'Hiring template')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              <p className="text-xs text-muted-foreground">
                {t(
                  'createOffer.form.hiringTemplateHelp',
                  'Choose which hiring workflow this offer will follow.'
                )}
              </p>
              <CustomSelect
                options={templates.map((tpl) => ({
                  value: String(tpl.id),
                  label: tpl.is_default
                    ? `${tpl.name} (${t('common.default', 'Default')})`
                    : tpl.name,
                }))}
                value={selectedTemplateId != null ? String(selectedTemplateId) : ''}
                onChange={(v) => setSelectedTemplateId(v ? Number(v) : null)}
                placeholder={t(
                  'createOffer.form.hiringTemplatePlaceholder',
                  'Select a hiring template'
                )}
                disabled={!!id && applications.length > 0}
              />
            </CardContent>
          </Card>
        </div>

        <form id="create-offer-form" onSubmit={handleSubmit(onSubmit, onSubmitErrors)} className="space-y-6 md:space-y-8">
          {/* Warning when fields are disabled */}
          {hasMatchingApplied && id && (
            <Alert variant="warning" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{t('common.warning', 'Warning')}</AlertTitle>
              <AlertDescription>
                {t('offerDetail.cannotEditAfterMatching', 'Some fields cannot be modified because matching has been applied to this offer.')}
              </AlertDescription>
            </Alert>
          )}
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Briefcase className="h-5 w-5" />
                <span>{t('createOffer.sections.basicInfo')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 md:space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <FormField
                  label={t('createOffer.form.jobTitle')}
                  required
                  error={errors.job_title?.message}
                >
                  <Input
                    {...register('job_title', {
                      onChange: () => trigger('job_title')
                    })}
                    onBlur={() => trigger('job_title')}
                    placeholder={t('createOffer.form.jobTitlePlaceholder')}
                    className={errors.job_title ? 'border-destructive' : ''}
                  />
                </FormField>

                <FormField
                  label={t('createOffer.form.department')}
                >
                  <Controller
                    name="department"
                    control={control}
                    render={({ field }) => (
                      <DepartmentSelect
                        value={field.value || ''}
                        onChange={(val) => {
                          field.onChange(val)
                          trigger('department')
                        }}
                        onBlur={field.onBlur}
                        placeholder={t('createOffer.form.departmentPlaceholder')}
                        error={errors.department?.message}
                        defaultDepartments={defaultDepartments}
                      />
                    )}
                  />
                </FormField>

                <FormField
                  label={t('createOffer.form.contractType')}
                  error={errors.contract_type?.message}
                >
                  <CustomSelect
                    value={watch('contract_type')}
                    onChange={(value) => setValue('contract_type', value)}
                    onBlur={() => trigger('contract_type')}
                    options={contractTypes}
                    placeholder={t('createOffer.form.contractTypePlaceholder')}
                  />
                </FormField>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium leading-none">
                      {t('createOffer.form.experienceLevel')}
                    </label>
                    <div className="inline-flex items-center gap-1.5">
                      <span className="text-xs text-muted-foreground max-[1149px]:text-[11px]">
                        {t('offerDetail.includeInternshipExperience', 'Exp. stage')}
                      </span>
                      <Switch
                        checked={includeInternshipExperience}
                        onCheckedChange={setIncludeInternshipExperience}
                        disabled={hasMatchingApplied}
                      />
                    </div>
                  </div>
                  <Input
                    type="number"
                    {...register('experience_level', {
                      onChange: () => trigger('experience_level')
                    })}
                    placeholder={t('createOffer.form.experienceLevelPlaceholder')}
                    className={errors.experience_level ? 'border-destructive' : ''}
                    min={0}
                    step={1}
                    onBlur={() => trigger('experience_level')}
                    readOnly={hasMatchingApplied}
                  />
                  {errors.experience_level?.message && (
                    <p className="text-sm text-destructive">{errors.experience_level?.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <FormField
                  label={t('createOffer.form.remoteAllowed')}
                  description={t('createOffer.form.remoteAllowedDescription')}
                >
                  <Controller
                    name="remote_allowed"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(checked)}
                        aria-label={t('createOffer.form.remoteAllowed')}
                      />
                    )}
                  />
                </FormField>
              </div>
            </CardContent>
          </Card>

          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Target className="h-5 w-5" />
                <span>{t('createOffer.sections.description')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FormField
                label={t('createOffer.form.description')}
                required
                error={errors.description?.message}
                description={t('createOffer.form.descriptionHelp')}
              >
                <Controller
                  name="description_text"
                  control={control}
                  render={({ field }) => (
                    <SimpleRichTextEditor
                      value={field.value || ''}
                      onChange={(value) => {
                        field.onChange(value)
                        // Also update description for backward compatibility
                        setValue('description', value)
                      }}
                      placeholder={t('createOffer.form.descriptionPlaceholder')}
                      height={300}
                      disabled={hasMatchingApplied}
                    />
                  )}
                />
              </FormField>
            </CardContent>
          </Card>

          {/* Requirements and Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('createOffer.sections.requirements')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Controller
                  name="requirements"
                  control={control}
                  render={({ field }) => (
                    <SingleFieldWithLabels
                      items={field.value || []}
                      onItemsChange={(items) => {
                        field.onChange(items)
                        setTimeout(() => trigger('requirements'), 100)
                      }}
                      onBlur={() => {
                        field.onBlur()
                        trigger('requirements')
                      }}
                      placeholder={t('createOffer.form.requirementPlaceholder')}
                      error={errors.requirements?.message}
                      description={t('createOffer.form.requirementsHelp')}
                      readOnly={hasMatchingApplied}
                    />
                  )}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('createOffer.sections.benefits')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Controller
                  name="benefits"
                  control={control}
                  render={({ field }) => (
                    <SingleFieldWithLabels
                      items={field.value || []}
                      onItemsChange={field.onChange}
                      placeholder={t('createOffer.form.benefitPlaceholder')}
                      error={errors.benefits?.message}
                      description={t('createOffer.form.benefitsHelp')}
                      readOnly={hasMatchingApplied}
                    />
                  )}
                />
              </CardContent>
            </Card>
          </div>

          {/* Additional Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('createOffer.sections.educationLevel')}</CardTitle>
              </CardHeader>
              <CardContent>
                <FormField
                  label={t('createOffer.form.educationLevel')}
                  error={errors.education_level?.message}
                >
                  <Controller
                    name="education_level"
                    control={control}
                    render={({ field }) => (
                      <CustomSelect
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={() => {
                          field.onBlur()
                          trigger('education_level')
                        }}
                        options={[
                          { value: 'Bac', label: 'Bac' },
                          { value: 'Bac+2', label: 'Bac +2' },
                          { value: 'Bac+3', label: 'Bac +3' },
                          { value: 'Bac+5', label: 'Bac +5' },
                          { value: 'Bac+8', label: 'Bac +8' }
                        ]}
                        placeholder={t('createOffer.form.educationLevelPlaceholder')}
                        disabled={hasMatchingApplied}
                      />
                    )}
                  />
                </FormField>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('createOffer.sections.languages')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Controller
                  name="languages"
                  control={control}
                  render={({ field }) => (
                    <LanguageField
                      items={field.value || []}
                      onItemsChange={(items) => {
                        field.onChange(items)
                        setTimeout(() => trigger('languages'), 100)
                      }}
                      onBlur={() => {
                        field.onBlur()
                        trigger('languages')
                      }}
                      placeholder={t('createOffer.form.languagePlaceholder')}
                      error={errors.languages?.message}
                      description={t('createOffer.form.languagesHelp')}
                      readOnly={hasMatchingApplied}
                    />
                  )}
                />
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('createOffer.sections.certifications')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Controller
                  name="certifications"
                  control={control}
                  render={({ field }) => (
                    <SingleFieldWithLabels
                      items={field.value || []}
                      onItemsChange={(items) => {
                        field.onChange(items)
                        setTimeout(() => trigger('certifications'), 100)
                      }}
                      onBlur={() => {
                        field.onBlur()
                        trigger('certifications')
                      }}
                      placeholder={t('createOffer.form.certificationPlaceholder')}
                      error={errors.certifications?.message}
                      description={t('createOffer.form.certificationsHelp')}
                      readOnly={hasMatchingApplied}
                    />
                  )}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('createOffer.sections.mainMissions')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Controller
                  name="main_missions"
                  control={control}
                  render={({ field }) => (
                    <SingleFieldWithLabels
                      items={field.value || []}
                      onItemsChange={(items) => {
                        field.onChange(items)
                        setTimeout(() => trigger('main_missions'), 100)
                      }}
                      onBlur={() => {
                        field.onBlur()
                        trigger('main_missions')
                      }}
                      placeholder={t('createOffer.form.missionPlaceholder')}
                      error={errors.main_missions?.message}
                      description={t('createOffer.form.mainMissionsHelp')}
                      readOnly={hasMatchingApplied}
                    />
                  )}
                />
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t('createOffer.sections.softSkills')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Controller
                name="soft_skills"
                control={control}
                render={({ field }) => (
                  <SingleFieldWithLabels
                    items={field.value}
                    onItemsChange={(items) => {
                      field.onChange(items)
                      setTimeout(() => trigger('soft_skills'), 100)
                    }}
                    onBlur={() => {
                      field.onBlur()
                      trigger('soft_skills')
                    }}
                    placeholder={t('createOffer.form.softSkillPlaceholder')}
                    error={errors.soft_skills?.message}
                    description={t('createOffer.form.softSkillsHelp')}
                    readOnly={hasMatchingApplied}
                  />
                )}
              />
            </CardContent>
          </Card>

          {/* AI Matching Configuration */}
          <Card data-ai-matching-section>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Percent className="h-5 w-5" />
                <span>{t('createOffer.sections.aiMatching')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {t('createOffer.form.aiMatchingDescription')}
                </p>

                {activeEvaluations.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p className="text-sm">
                      {t('createOffer.form.aiMatchingNoEvaluations', 'No evaluations available. Add related data (benefits, education level, languages, requirements, etc.) to configure AI matching.')}
                    </p>
                  </div>
                ) : (() => {
                  // Define the same order as AI Matching Weights
                  const aiMatchingWeightsOrder = [
                    'hard_skills',
                    'soft_skills',
                    'certifications',
                    'nice_to_have',
                    'languages',
                    'experience_years',
                    'niveau_etude',
                    'main_missions',
                  ] as const

                  // Order activeEvaluations according to aiMatchingWeightsOrder
                  const orderedActiveEvaluations = [
                    ...aiMatchingWeightsOrder.filter(key => activeEvaluations.includes(key)),
                    ...activeEvaluations.filter(key => !aiMatchingWeightsOrder.includes(key as any))
                  ]

                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {orderedActiveEvaluations.map((key) => {
                        const numericValue = clampAiMatchingWeight(
                          Number(aiMatching?.[key as keyof typeof aiMatching]) || 0
                        )
                        const colorMap: Record<string, string> = {
                          hard_skills: '#3b82f6',
                          soft_skills: '#10b981',
                          experience_years: '#f59e0b',
                          niveau_etude: '#ef4444',
                          languages: '#06b6d4',
                          certifications: '#f97316',
                          main_missions: '#84cc16',
                          nice_to_have: '#a78bfa'
                        }
                        const getWeightColor = (key: string) => {
                          return colorMap[key] || '#9ca3af'
                        }
                        const weightCategoryOptions = [
                          { value: 1, label: t('offerDetail.veryLowPriority', 'Very Low Priority') },
                          { value: 3, label: t('offerDetail.lowPriority', 'Low Priority') },
                          { value: 5, label: t('offerDetail.mediumPriority', 'Medium Priority') },
                          { value: 7, label: t('offerDetail.highPriority', 'High Priority') },
                          { value: 9, label: t('offerDetail.veryHighPriority', 'Very High Priority') }
                        ]
                        const categoryValue = weightCategoryOptions.some(option => option.value === numericValue)
                          ? String(numericValue)
                          : ''
                        return (
                          <div key={key} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <label className="text-sm font-medium capitalize text-muted-foreground max-[1149px]:text-xs">
                                {t(`createOffer.form.aiMatching.${key}`)}
                              </label>
                              <div className="relative">
                                <Input
                                  type="number"
                                  min={0}
                                  max={10}
                                  value={numericValue}
                                  onChange={(e) => {
                                    const newValue = Number(e.target.value) || 0
                                    handleAiMatchingWeightChange(key, newValue)
                                    setTimeout(() => trigger('ai_matching'), 100)
                                  }}
                                  onBlur={() => trigger('ai_matching')}
                                  className="w-20 h-8 text-right text-sm"
                                  disabled={hasMatchingApplied}
                                />
                              </div>
                            </div>
                            <input
                              type="range"
                              min={0}
                              max={10}
                              value={numericValue}
                              onChange={(e) => {
                                handleAiMatchingWeightChange(key, Number(e.target.value))
                                setTimeout(() => trigger('ai_matching'), 100)
                              }}
                              onBlur={() => trigger('ai_matching')}
                              className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                              disabled={hasMatchingApplied}
                              style={{
                                accentColor: getWeightColor(key),
                                background: `linear-gradient(to right, ${getWeightColor(key)} 0%, ${getWeightColor(key)} ${(numericValue / 10) * 100}%, #e5e7eb ${(numericValue / 10) * 100}%, #e5e7eb 100%)`
                              }}
                            />
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span>{t('offerDetail.priorityCategory', 'Priority category')}</span>
                              <select
                                value={categoryValue}
                                onChange={(e) => {
                                  if (e.target.value === '') return
                                  const nextValue = Number(e.target.value)
                                  if (!Number.isFinite(nextValue)) return
                                  handleAiMatchingWeightChange(key, nextValue)
                                  setTimeout(() => trigger('ai_matching'), 100)
                                }}
                                className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground"
                                disabled={hasMatchingApplied}
                              >
                                <option value="">{t('offerDetail.customPriority', 'Custom')}</option>
                                {weightCategoryOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )
                })()}


                {activeEvaluations.length > 0 && (() => {
                  // Use the same order as AI Matching Weights
                  const aiMatchingWeightsOrder = [
                    'hard_skills',
                    'soft_skills',
                    'certifications',
                    'nice_to_have',
                    'languages',
                    'experience_years',
                    'niveau_etude',
                    'main_missions',
                  ] as const

                  // Order activeEvaluations according to aiMatchingWeightsOrder
                  const orderedActiveEvaluations = [
                    ...aiMatchingWeightsOrder.filter(key => activeEvaluations.includes(key)),
                    ...activeEvaluations.filter(key => !aiMatchingWeightsOrder.includes(key as any))
                  ]

                  const colorMap: Record<string, string> = {
                    hard_skills: '#3b82f6',
                    soft_skills: '#10b981',
                    experience_years: '#f59e0b',
                    niveau_etude: '#ef4444',
                    languages: '#06b6d4',
                    certifications: '#f97316',
                    main_missions: '#84cc16',
                    nice_to_have: '#a78bfa'
                  }

                  // Build chart data (raw values)
                  const chartData = orderedActiveEvaluations.map(key => ({
                    name: t(`createOffer.form.aiMatching.${key}`),
                    value: aiMatching?.[key as keyof typeof aiMatching] ?? 0,
                    field: key,
                    color: colorMap[key] || '#9ca3af'
                  }))

                  // Normalize values to percentages for visualization only
                  const rawTotal = chartData.reduce((sum, d) => sum + (Number.isFinite(d.value) ? d.value : 0), 0)
                  const normalizedChartData = chartData.map(item => ({
                    ...item,
                    value: rawTotal > 0 ? (item.value / rawTotal) * 100 : 0
                  }))

                  return (
                    <>
                      <div className="flex items-center justify-center">
                        <DraggablePieChart
                          data={normalizedChartData}
                          onValueChange={(field, value) => {
                            const rawValue = rawTotal > 0 ? (value / 100) * rawTotal : value
                            handleAiMatchingWeightChange(field, Math.round(rawValue))
                          }}
                        />
                      </div>
                    </>
                  )
                })()}
              </div>
            </CardContent>
          </Card>

        </form>

        {/* Action Buttons at Bottom - Three buttons: Cancel, Publish job offer (if draft), Update */}
        <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={navigateBackToOffer}
            className="flex-1 sm:flex-initial sm:w-auto h-10 whitespace-nowrap"
          >
            {t('common.cancel')}
          </Button>
          {id && offerStatus === 'draft' && (
            <Button
              type="button"
              variant="outline"
              onClick={publishDraft}
              disabled={isPublishing}
              className="flex-1 sm:flex-initial sm:w-auto h-10 flex items-center justify-center space-x-2 bg-green-600 text-white border-green-600 hover:bg-green-700 hover:border-green-700 hover:text-white whitespace-nowrap"
            >
              <Save className="h-4 w-4 flex-shrink-0" />
              <span>
                {isPublishing ? t('common.saving') : t('createOffer.form.publish')}
              </span>
            </Button>
          )}
          <Button
            type="submit"
            form="create-offer-form"
            disabled={isSubmitting}
            className="flex-1 sm:flex-initial sm:w-auto h-10 flex items-center justify-center space-x-2 whitespace-nowrap"
          >
            <Save className="h-4 w-4 flex-shrink-0" />
            <span>
              {isSubmitting ? t('common.saving') : (id ? t('common.update') : t('createOffer.form.publish'))}
            </span>
          </Button>
        </div>
      </div>

      {/* Publish Confirmation Modal */}
      <Modal
        isOpen={showPublishConfirm}
        onClose={() => {
          if (!isPublishing) setShowPublishConfirm(false)
        }}
        title={t('offers.confirmPublish.title', 'Publish Offer')}
      >
        <div className="space-y-4">
          <div>
            <p className="text-sm text-foreground mb-3">
              {t('offers.confirmPublish.message', 'Are you sure you want to publish this offer?')}
            </p>
            <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-md p-3">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-2">
                {t('offers.confirmPublish.warning', 'Important:')}
              </p>
              <ul className="text-xs text-amber-800 dark:text-amber-200 space-y-1">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>{t('offers.confirmPublish.cannotDraft', 'You cannot return it to draft status')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>{t('offers.confirmPublish.onlyArchive', 'Afterwards you can close applications or archive it')}</span>
                  </li>
              </ul>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setShowPublishConfirm(false)}
              disabled={isPublishing}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={confirmPublish}
              disabled={isPublishing}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {isPublishing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('common.publishing', 'Publishing')}
                </>
              ) : (
                t('offers.confirmPublish.confirm', 'Publish Offer')
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Zero Weights Warning Modal */}
      <Modal
        isOpen={showZeroWeightsWarning}
        onClose={() => setShowZeroWeightsWarning(false)}
        title={t('common.warning', 'Warning')}
      >
        <div className="space-y-4">
          <div className="flex items-start space-x-3 text-amber-600 dark:text-amber-500 bg-amber-50 dark:bg-amber-950/30 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
            <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
            <p className="text-sm font-medium">
              {t('offers.messages.cannotPublishZeroWeights', "Il n’est pas possible d’activer l’offre si tous les poids des champs sont à 0, Au moins un poids doit être différent de 0. Veuillez d'abord modifier les poids avant d'activer l'offre.")}
            </p>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={() => setShowZeroWeightsWarning(false)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {t('common.ok', 'OK')}
            </Button>
          </div>
        </div>
      </Modal>

      <RecommendedSkillsModal
        isOpen={showRecommendedSkillsModal}
        onClose={() => {
          setShowRecommendedSkillsModal(false)
          if (id) {
            navigate(`/offers/${id}`, { state: { initialTab: 'details' } })
          } else if (createdOffer) {
            navigate(`/offers/${createdOffer.id}`, { state: { initialTab: 'details' } })
          } else {
            navigate('/offers')
          }
        }}
        onAccept={handleAcceptRecommendedSkills}
        skills={recommendedSkillsData}
        isLoading={isSavingRecommendedSkills}
        initialWeights={{
          hard_skills: createdOffer?.ai_matching_weights?.hard_skills ?? 2,
          soft_skills: createdOffer?.ai_matching_weights?.soft_skills ?? 2,
          nice_to_have: createdOffer?.ai_matching_weights?.nice_to_have ?? 0,
        }}
      />
    </div>
  )
}
